import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { enumTruckStatus } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { DateField } from '@rematter/ui-web';
import {
  Radio,
  RadioGroupField,
  TextField,
  WeightField,
  SelectCountry,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { TransportationTypeSelect } from '~/modules/transportation/components/@shared/TransportationTypeSelect';
import { VehicleVinTextField } from '~/modules/transportation/components/@shared/VehicleVinTextField';
import {
  $truckForm,
  $updateTruckForm,
} from '~/modules/transportation/state/truckForm.localState';
import { $localization } from '~/shared/state/preferences.state';

type TruckFormProps = {
  isEditing?: boolean;
};

export const TruckForm = (_props: TruckFormProps) => {
  const t = useTranslations();
  const [states, _setStates] = React.useState<string[]>([]);
  const localization = useAtomValue($localization);
  const form = useAtomValue($truckForm);
  const updateForm = useSetAtom($updateTruckForm);

  React.useEffect(() => {
    if (form.country === '') {
      updateForm({
        country: localization.defaultCountry,
      });
    }
  }, [form, updateForm, localization.defaultCountry]);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: (typeof form)[K]) => {
      updateForm({
        [field]: value,
      });
    };

  return (
    <div className="@TruckForm flex flex-col gap-4 p-4">
      <RadioGroupField
        label={t.trucksView.form.status}
        defaultValue={form.status}
        onChange={(value: string) =>
          handleFieldChange('status')(value as typeof form.status)
        }
        orientation="horizontal"
        isRequired
      >
        {Object.values(enumTruckStatus).map((status) => (
          <Radio key={status} value={status}>
            {t.trucksView.status[status]}
          </Radio>
        ))}
      </RadioGroupField>

      <TransportationTypeSelect
        label={t.trucksView.form.transportationType}
        value={form.transportationTypeId}
        onChange={(value) =>
          handleFieldChange('transportationTypeId')(value ? String(value) : '')
        }
        isRequired
      />

      <div className="grid grid-cols-3 grid-rows-1 gap-4">
        <VehicleVinTextField
          label={t.trucksView.form.vin}
          value={form.vin}
          onChange={handleFieldChange('vin')}
          onSuccess={(vehicleInfo) => {
            updateForm({
              model: `${vehicleInfo.Make} ${vehicleInfo.Model} ${vehicleInfo.ModelYear}`,
            });
          }}
        />

        <div className="col-span-2">
          <TextField
            label={t.trucksView.form.model}
            value={form.model}
            onChange={handleFieldChange('model')}
            isRequired
          />
        </div>
      </div>

      <div className="grid grid-cols-2 grid-rows-1 gap-4">
        <TextField
          label={t.trucksView.form.licensePlate}
          value={form.licensePlate}
          onChange={handleFieldChange('licensePlate')}
          isRequired
        />

        <DateField
          label={t.trucksView.form.licensePlateExpiration}
          value={form.licensePlateExpiration}
          onChange={(value: CalendarDate | null) =>
            handleFieldChange('licensePlateExpiration')(value ?? undefined)
          }
        />
      </div>

      <WeightField
        label={t.trucksView.form.capacity}
        value={form.capacity}
        defaultUnit="Pounds"
        onWeightChange={(value: number | null) =>
          handleFieldChange('capacity')(value ?? 0)
        }
        isRequired
      />

      <SelectCountry
        label={t.trucksView.form.country}
        value={form.country}
        onSelectionChange={(value) => handleFieldChange('country')(value ?? '')}
        isRequired
      />

      {states.length > 0 ? (
        <TextField
          label={t.trucksView.form.state}
          value={form.state}
          onChange={(value: string) => handleFieldChange('state')(value)}
          isRequired
          isDisabled={!form.country}
        />
      ) : (
        <TextField
          label={t.trucksView.form.state}
          value={form.state}
          onChange={handleFieldChange('state')}
        />
      )}

      <TextField
        label={t.trucksView.form.notes}
        value={form.notes}
        onChange={handleFieldChange('notes')}
        type="text"
      />
    </div>
  );
};
